import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { MessageSquare, Server, Send, Loader2, CheckCircle, Eye, EyeOff, Phone, ExternalLink } from 'lucide-react';

const TwilioSettingsPage = () => {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('settings');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showToken, setShowToken] = useState(false);
  
  const [twilioSettings, setTwilioSettings] = useState({
    account_sid: '',
    auth_token: '',
    phone_number: '',
    configured: false
  });
  
  const [sentSMS, setSentSMS] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, smsRes] = await Promise.all([
        api().get('/twilio-settings'),
        api().get('/sent-sms')
      ]);
      
      if (settingsRes.data.configured) {
        setTwilioSettings({ ...settingsRes.data, auth_token: '' });
      }
      setSentSMS(smsRes.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!twilioSettings.account_sid || !twilioSettings.phone_number) {
      toast.error('Please fill in Account SID and Phone Number');
      return;
    }
    
    setSaving(true);
    try {
      await api().put('/twilio-settings', twilioSettings);
      toast.success('Twilio settings saved successfully');
      setTwilioSettings({ ...twilioSettings, configured: true });
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSettings = async () => {
    if (!twilioSettings.account_sid || !twilioSettings.auth_token || !twilioSettings.phone_number) {
      toast.error('Please fill in all fields including Auth Token to test');
      return;
    }
    
    setTesting(true);
    try {
      await api().post('/twilio-settings/test', twilioSettings);
      toast.success('Test SMS sent successfully! Check your phone.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Twilio test failed');
    } finally {
      setTesting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="twilio-settings-page">
      <div>
        <h1 className="text-2xl font-bold">Twilio SMS Settings</h1>
        <p className="text-muted-foreground">Configure your Twilio account to send SMS to students</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="settings" data-testid="tab-settings">
            <Server className="w-4 h-4 mr-2" />
            Twilio Configuration
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <Send className="w-4 h-4 mr-2" />
            Sent SMS
          </TabsTrigger>
        </TabsList>

        {/* Twilio Configuration Tab */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Twilio Account Configuration
              </CardTitle>
              <CardDescription>
                Configure your Twilio account to send SMS. Get your credentials from{' '}
                <a 
                  href="https://console.twilio.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline inline-flex items-center gap-1"
                >
                  Twilio Console <ExternalLink className="w-3 h-3" />
                </a>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="account_sid">Account SID *</Label>
                  <Input
                    id="account_sid"
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    value={twilioSettings.account_sid}
                    onChange={(e) => setTwilioSettings({ ...twilioSettings, account_sid: e.target.value })}
                    data-testid="account-sid-input"
                  />
                  <p className="text-xs text-muted-foreground">Found on your Twilio Console Dashboard</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="auth_token">Auth Token *</Label>
                  <div className="relative">
                    <Input
                      id="auth_token"
                      type={showToken ? "text" : "password"}
                      placeholder="••••••••••••••••••••••••••••••••"
                      value={twilioSettings.auth_token}
                      onChange={(e) => setTwilioSettings({ ...twilioSettings, auth_token: e.target.value })}
                      data-testid="auth-token-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full"
                      onClick={() => setShowToken(!showToken)}
                    >
                      {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">Keep this secret and secure</p>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="phone_number">Twilio Phone Number *</Label>
                  <Input
                    id="phone_number"
                    placeholder="+1234567890"
                    value={twilioSettings.phone_number}
                    onChange={(e) => setTwilioSettings({ ...twilioSettings, phone_number: e.target.value })}
                    data-testid="phone-number-input"
                  />
                  <p className="text-xs text-muted-foreground">Your Twilio phone number in E.164 format (e.g., +1234567890). Buy one from Twilio Console → Phone Numbers</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button onClick={handleSaveSettings} disabled={saving} data-testid="save-twilio-btn">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Save Settings
                </Button>
                <Button variant="outline" onClick={handleTestSettings} disabled={testing} data-testid="test-twilio-btn">
                  {testing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  <Send className="w-4 h-4 mr-2" />
                  Send Test SMS
                </Button>
              </div>
              
              {twilioSettings.configured && (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="w-5 h-5" />
                  <span>Twilio is configured and ready to send SMS</span>
                </div>
              )}
              
              <div className="p-4 bg-muted rounded-lg">
                <h4 className="font-medium mb-2">Setup Instructions</h4>
                <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
                  <li>Sign up or log in to <a href="https://www.twilio.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Twilio</a></li>
                  <li>Go to Console Dashboard to find your Account SID and Auth Token</li>
                  <li>Buy a phone number from Phone Numbers → Buy a Number</li>
                  <li>Enter the credentials above and save</li>
                  <li>Click "Send Test SMS" to verify your configuration</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sent SMS History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="w-5 h-5" />
                Sent SMS History
              </CardTitle>
              <CardDescription>
                View all SMS messages sent from your organization
              </CardDescription>
            </CardHeader>
            <CardContent>
              {sentSMS.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No SMS messages sent yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>To</TableHead>
                      <TableHead>Message</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Sent At</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sentSMS.map((sms) => (
                      <TableRow key={sms.id}>
                        <TableCell className="font-mono text-sm">{sms.to_phone}</TableCell>
                        <TableCell className="max-w-xs truncate">{sms.message}</TableCell>
                        <TableCell>
                          <Badge variant={sms.status === 'failed' ? 'destructive' : 'default'}>
                            {sms.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(sms.sent_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TwilioSettingsPage;
